import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, UserCog, Check, Building2 } from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";

const inputCls =
  "w-full bg-ink-900 border border-ink-500 text-ash-100 focus:border-brand outline-none px-3 py-2 text-sm placeholder:text-ash-500";

const ROLE_LABEL = { admin: "Administrador", seller: "Vendedor" };

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const confirm = useConfirm();

  const refresh = async () => {
    try {
      const [u, c] = await Promise.all([api.get("/admin/users"), api.get("/admin/companies")]);
      setUsers(u.data || []);
      setCompanies(c.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudieron cargar los usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const newUser = () =>
    setEditing({
      _new: true,
      email: "",
      name: "",
      password: "",
      role: "seller",
      is_active: true,
      company_ids: [],
    });

  const editUser = (u) => setEditing({ ...u, password: "" });

  const remove = async (u) => {
    if (u.id === me?.id) {
      toast.error("No puedes eliminar tu propia cuenta");
      return;
    }
    const ok = await confirm({
      title: `¿Eliminar al usuario "${u.name}"?`,
      message: "Sus leads, cotizaciones, prospectos y propuestas quedarán sin dueño (solo el administrador los verá).",
      confirmLabel: "Eliminar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Usuario eliminado");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo eliminar");
    }
  };

  const save = async () => {
    if (!editing) return;
    const payload = {
      email: editing.email?.trim().toLowerCase(),
      name: editing.name?.trim(),
      role: editing.role,
      is_active: !!editing.is_active,
      company_ids: editing.company_ids || [],
    };
    if (editing._new) payload.password = editing.password || "";
    else if (editing.password) payload.password = editing.password;

    if (!payload.email) return toast.error("El email es obligatorio");
    if (!payload.name) return toast.error("El nombre es obligatorio");
    if (editing._new && (!payload.password || payload.password.length < 4))
      return toast.error("La contraseña debe tener al menos 4 caracteres");

    try {
      let userId;
      if (editing._new) {
        const r = await api.post("/admin/users", payload);
        userId = r.data.id;
        toast.success("Usuario creado");
      } else {
        const { company_ids, ...rest } = payload;
        await api.patch(`/admin/users/${editing.id}`, rest);
        await api.put(`/admin/users/${editing.id}/companies`, { company_ids });
        userId = editing.id;
        toast.success("Usuario actualizado");
      }
      setEditing(null);
      refresh();
      return userId;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar");
    }
  };

  const toggleCompany = (id) => {
    setEditing((p) => {
      const set = new Set(p.company_ids || []);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...p, company_ids: [...set] };
    });
  };

  const companyById = useMemo(() => {
    const m = {};
    companies.forEach((c) => (m[c.id] = c));
    return m;
  }, [companies]);

  return (
    <div data-testid="admin-users-page" className="p-6 md:p-8">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">Administración</div>
          <h1 className="font-display font-bold text-2xl md:text-3xl text-ash-100">Vendedores</h1>
          <p className="text-ash-300 text-sm mt-2 max-w-2xl">
            Crea y administra a los vendedores. Cada uno verá únicamente los leads, cotizaciones, prospectos y propuestas que cree, y solo podrá asignar a las empresas que tú le marques aquí.
          </p>
        </div>
        <button
          data-testid="user-create"
          onClick={newUser}
          className="inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-2.5 uppercase tracking-widest text-xs"
        >
          <Plus size={14} /> Nuevo vendedor
        </button>
      </div>

      {loading ? (
        <div className="text-ash-300 text-sm">Cargando…</div>
      ) : users.length === 0 ? (
        <div className="border border-dashed border-ink-500 p-10 text-center text-ash-300">
          Aún no hay usuarios.
        </div>
      ) : (
        <div className="border border-ink-500 bg-ink-800/40">
          <table className="w-full text-sm">
            <thead className="bg-ink-800 text-ash-500 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="text-left px-4 py-3 font-mono">Nombre</th>
                <th className="text-left px-4 py-3 font-mono">Email</th>
                <th className="text-left px-4 py-3 font-mono">Rol</th>
                <th className="text-left px-4 py-3 font-mono">Empresas</th>
                <th className="text-left px-4 py-3 font-mono">Estado</th>
                <th className="text-right px-4 py-3 font-mono">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr
                  key={u.id}
                  data-testid={`user-row-${u.id}`}
                  className="border-t border-ink-500 hover:bg-ink-800/60"
                >
                  <td className="px-4 py-3 text-ash-100">
                    {u.name}
                    {u.id === me?.id && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest font-mono text-brand">(tú)</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ash-300 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 ${
                        u.role === "admin" ? "bg-brand text-ink-800" : "bg-ink-500 text-ash-100"
                      }`}
                    >
                      {ROLE_LABEL[u.role] || u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-ash-300 text-xs">
                    {u.role === "admin" ? (
                      <span className="text-ash-500 italic">todas (admin)</span>
                    ) : (u.company_ids || []).length === 0 ? (
                      <span className="text-ash-500">ninguna</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(u.company_ids || []).map((cid) => (
                          <span
                            key={cid}
                            className="inline-flex items-center gap-1 bg-ink-700 border border-ink-500 px-1.5 py-0.5 text-[10px]"
                          >
                            <span
                              className="w-2 h-2"
                              style={{ background: companyById[cid]?.color || "#7BA6B0" }}
                            />
                            {companyById[cid]?.name || cid.slice(0, 8)}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] uppercase tracking-widest font-mono ${
                        u.is_active ? "text-emerald-400" : "text-ash-500"
                      }`}
                    >
                      {u.is_active ? "Activo" : "Deshabilitado"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      data-testid={`user-edit-${u.id}`}
                      onClick={() => editUser(u)}
                      className="inline-flex items-center gap-1 text-ash-300 hover:text-brand text-xs mr-2"
                    >
                      <Pencil size={12} /> Editar
                    </button>
                    {u.id !== me?.id && (
                      <button
                        data-testid={`user-delete-${u.id}`}
                        onClick={() => remove(u)}
                        className="inline-flex items-center gap-1 text-ash-300 hover:text-red-400 text-xs"
                      >
                        <Trash2 size={12} /> Eliminar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-ink-900/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            data-testid="user-editor"
            className="w-full max-w-2xl bg-ink-800 border border-ink-500 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-500">
              <div className="flex items-center gap-3">
                <UserCog size={18} className="text-brand" />
                <h2 className="font-display font-bold text-lg text-ash-100">
                  {editing._new ? "Nuevo vendedor" : "Editar usuario"}
                </h2>
              </div>
              <button
                data-testid="user-editor-close"
                onClick={() => setEditing(null)}
                className="text-ash-500 hover:text-ash-100"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Nombre completo">
                  <input
                    data-testid="user-name"
                    className={inputCls}
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    placeholder="Ej. Juan Pérez"
                  />
                </Field>
                <Field label="Email">
                  <input
                    data-testid="user-email"
                    type="email"
                    className={inputCls}
                    value={editing.email}
                    onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                    placeholder="vendedor@dosoft.cl"
                  />
                </Field>
                <Field label="Rol">
                  <select
                    data-testid="user-role"
                    className={inputCls}
                    value={editing.role}
                    onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                    disabled={editing.id === me?.id}
                  >
                    <option value="seller">Vendedor</option>
                    <option value="admin">Administrador</option>
                  </select>
                </Field>
                <Field
                  label={editing._new ? "Contraseña" : "Nueva contraseña (opcional)"}
                >
                  <input
                    data-testid="user-password"
                    type="password"
                    className={inputCls}
                    value={editing.password || ""}
                    onChange={(e) => setEditing({ ...editing, password: e.target.value })}
                    placeholder={editing._new ? "Mínimo 4 caracteres" : "Dejar vacío para no cambiar"}
                    autoComplete="new-password"
                  />
                </Field>
              </div>

              <div className="flex items-center gap-3">
                <input
                  id="user_active"
                  type="checkbox"
                  data-testid="user-active"
                  checked={!!editing.is_active}
                  onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })}
                  disabled={editing.id === me?.id}
                  className="accent-brand"
                />
                <label htmlFor="user_active" className="text-sm text-ash-100">
                  Usuario activo (puede iniciar sesión)
                </label>
              </div>

              {editing.role === "seller" && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 size={14} className="text-brand" />
                    <span className="text-xs uppercase tracking-widest text-ash-300 font-mono">
                      Empresas asignadas
                    </span>
                  </div>
                  {companies.length === 0 ? (
                    <div className="border border-dashed border-ink-500 p-4 text-sm text-ash-500">
                      Aún no hay empresas creadas. Crea una desde la sección "Empresas" para poder asignarla.
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                      {companies.map((c) => {
                        const checked = (editing.company_ids || []).includes(c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            data-testid={`user-company-toggle-${c.id}`}
                            onClick={() => toggleCompany(c.id)}
                            className={`flex items-center gap-3 px-3 py-2 border text-left text-sm transition-colors ${
                              checked
                                ? "border-brand bg-brand/10 text-ash-100"
                                : "border-ink-500 bg-ink-900 text-ash-300 hover:border-ash-500"
                            }`}
                          >
                            <span
                              className="w-2.5 h-2.5 shrink-0"
                              style={{ background: c.color || "#7BA6B0" }}
                            />
                            <span className="flex-1 truncate">{c.name}</span>
                            {checked && <Check size={14} className="text-brand" />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  data-testid="user-cancel"
                  onClick={() => setEditing(null)}
                  className="px-4 py-2 border border-ink-500 text-ash-300 hover:text-ash-100 text-sm"
                >
                  Cancelar
                </button>
                <button
                  data-testid="user-save"
                  onClick={save}
                  className="px-5 py-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover uppercase tracking-widest text-xs"
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-ash-500 font-mono mb-1">{label}</span>
      {children}
    </label>
  );
}
